import { _todayKey } from './triviaHelpers';
import { normalizeTriviaQuestion } from './triviaContent';

function _normalizeQuestions(raw) {
  const out = [];

  // Dedupe fuerte: por id si existe, y por contenido (texto+respuestas) para evitar
  // que una misma pregunta aparezca duplicada con ids distintos en el JSON.
  const seenId = new Set();
  const seenContent = new Set();

  const norm = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const hash32 = (str) => {
    // djb2 -> hex (stable, fast, no deps)
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) + h) ^ str.charCodeAt(i);
    }
    // >>>0 para uint32
    return (h >>> 0).toString(16);
  };

  (raw || []).forEach((q) => {
    if (!q || typeof q !== 'object') return;

    const normalizedQuestion = normalizeTriviaQuestion(q);
    if (!normalizedQuestion) return;
    const { text, answers } = normalizedQuestion;

    // Content key: texto normalizado + respuestas normalizadas (orden estable)
    const contentKey = norm(text) + '|' + answers.map(a => norm(a.text)).join('|');
    if (seenContent.has(contentKey)) return;

    // ID estable: si viene id lo usamos; si no, generamos hash del contenido (NO random)
    const incomingId = String(q.id || '').trim();
    const id = incomingId || `q_${hash32(contentKey)}`;
    if (seenId.has(id)) return;

    seenId.add(id);
    seenContent.add(contentKey);

    out.push({
      ...normalizedQuestion,
      id,
    });
  });

  return out;
}
async function _fetchJsonWithTimeout(url, ms = 4000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}

// Preferimos un JSON con 365 preguntas. (index.json es opcional; evitamos pedirlo para no generar 404 en dev)
const _TRIVIA_SOURCES = ['/assets/trivia/365.json'];
const _TRIVIA_CACHE_KEY = 'zodiacus_trivia_cache_v1';
const _TRIVIA_USED_CATEGORIES_KEY_PREFIX = 'zodiacus_trivia_used_categories_v1:';

// === Trivia: deterministic RNG helpers (avoid repeats + stable daily picks) ===
function _hashString(str) {
  // djb2 (uint32)
  const s = String(str || '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return (h >>> 0);
}

function _mulberry32(seed) {
  // returns a RNG function rand() -> [0,1)
  let a = (Number(seed) >>> 0);
  return function rand() {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function _seededShuffle(arr, rand) {
  const out = Array.isArray(arr) ? arr.slice() : [];
  const r = (typeof rand === 'function') ? rand : Math.random;
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

// === Flag "ya jugó hoy" por usuario (helpers fuera del array) ===
function _playedKey(uid) {
  const safe = (uid && String(uid)) ? String(uid) : 'anon';
  return `trivia_played_day:${safe}`;
}
export function _getPlayedToday(uid) {
  try { return localStorage.getItem(_playedKey(uid)) === _todayKey(); } catch { return false; }
}
export function _setPlayedToday(uid, dayKey) {
  try { localStorage.setItem(_playedKey(uid), dayKey); } catch {}
}
export function _clearPlayedToday(uid) {
  try { localStorage.removeItem(_playedKey(uid)); } catch {}
}

export const TRIVIA_LOCAL_FALLBACK = [
  {
    id: 'aries_regente_1',
    category: 'signos',
    difficulty: 'easy',
    text: '¿Cuál es el planeta regente de Aries?',
    localized: {
      text: {
        es: '¿Cuál es el planeta regente de Aries?',
        en: 'Which planet rules Aries?'
      },
      explanation: {
        es: 'Aries está regido por Marte.',
        en: 'Aries is ruled by Mars.'
      }
    },
    answers: [
      { id: 'mars', text: 'Marte', label: { es: 'Marte', en: 'Mars' }, correct: true },
      { id: 'venus', text: 'Venus', label: { es: 'Venus', en: 'Venus' }, correct: false },
      { id: 'mercury', text: 'Mercurio', label: { es: 'Mercurio', en: 'Mercury' }, correct: false },
      { id: 'saturn', text: 'Saturno', label: { es: 'Saturno', en: 'Saturn' }, correct: false }
    ],
    correctOptionId: 'mars',
    explanation: 'Aries está regido por Marte.'
  },
  {
    id: 'libra_elemento_1',
    category: 'elementos',
    difficulty: 'easy',
    text: '¿Cuál es el elemento de Libra?',
    localized: {
      text: {
        es: '¿Cuál es el elemento de Libra?',
        en: 'What is Libra’s element?'
      }
    },
    answers: [
      { id: 'air', text: 'Aire', label: { es: 'Aire', en: 'Air' }, correct: true },
      { id: 'water', text: 'Agua', label: { es: 'Agua', en: 'Water' }, correct: false },
      { id: 'fire', text: 'Fuego', label: { es: 'Fuego', en: 'Fire' }, correct: false },
      { id: 'earth', text: 'Tierra', label: { es: 'Tierra', en: 'Earth' }, correct: false }
    ],
    correctOptionId: 'air'
  },
  {
    id: 'escorpio_rasgo_1',
    category: 'signos',
    difficulty: 'easy',
    text: 'Un rasgo típico de Escorpio es…',
    localized: {
      text: {
        es: 'Un rasgo típico de Escorpio es…',
        en: 'A typical Scorpio trait is…'
      }
    },
    answers: [
      { id: 'intensity', text: 'Intensidad', label: { es: 'Intensidad', en: 'Intensity' }, correct: true },
      { id: 'indifference', text: 'Indiferencia', label: { es: 'Indiferencia', en: 'Indifference' }, correct: false },
      { id: 'extreme_detachment', text: 'Desapego extremo', label: { es: 'Desapego extremo', en: 'Extreme detachment' }, correct: false },
      { id: 'superficiality', text: 'Superficialidad', label: { es: 'Superficialidad', en: 'Superficiality' }, correct: false }
    ],
    correctOptionId: 'intensity'
  }
];

export async function getDailyTriviaQuestions({ count = 8, categories = [], difficulties = [], userId = null } = {}) {
  const day = _todayKey();
  const safeUid = (userId && String(userId)) ? String(userId) : 'anon';

  // Cache por usuario (evita mezclar trivias entre cuentas)
  const CACHE_KEY = `${_TRIVIA_CACHE_KEY}:${safeUid}`;
  const USED_CATS_KEY = `${_TRIVIA_USED_CATEGORIES_KEY_PREFIX}${safeUid}`;
  const USED_Q_KEY = `zodiacus_trivia_used_questions_v1:${safeUid}`;
  let cachedSelection = null;
  let cachedCategory = null;

  // Helpers internos (persistencia de preguntas usadas)
  const _loadUsedQuestions = () => {
    try {
      const raw = localStorage.getItem(USED_Q_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const _saveUsedQuestions = (arr) => {
    try {
      // guardamos como mucho 2200 entradas para no inflar storage
      const safe = Array.isArray(arr) ? arr.slice(-2200) : [];
      localStorage.setItem(USED_Q_KEY, JSON.stringify(safe));
    } catch {}
  };
  const _pruneUsedQuestions = (arr, todayKey, { maxItems = 2000, maxDays = 400 } = {}) => {
    try {
      const list = Array.isArray(arr) ? arr : [];
      // conservamos solo items válidos
      const clean = list
        .map(x => ({ id: String(x?.id || '').trim(), day: String(x?.day || '').trim() }))
        .filter(x => x.id && /^\d{4}-\d{2}-\d{2}$/.test(x.day));

      // dedupe por id (mantener el más reciente)
      const byId = new Map();
      for (const it of clean) {
        const prev = byId.get(it.id);
        if (!prev || it.day > prev.day) byId.set(it.id, it);
      }
      let deduped = Array.from(byId.values());

      // prune por días (lexicográfico sirve con YYYY-MM-DD)
      deduped.sort((a, b) => String(b.day).localeCompare(String(a.day)));
      const allowedDays = new Set();
      for (const it of deduped) {
        allowedDays.add(it.day);
        if (allowedDays.size >= maxDays) break;
      }
      deduped = deduped.filter(it => allowedDays.has(it.day));

      // prune por tamaño
      if (deduped.length > maxItems) deduped = deduped.slice(0, maxItems);

      // volver a ordenar asc por day (no es necesario, pero deja prolijo)
      deduped.sort((a, b) => String(a.day).localeCompare(String(b.day)));
      return deduped;
    } catch {
      return [];
    }
  };

  // 1) Intentar devolver cache del día (misma trivia si recarga)
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    if (raw && raw.day === day && Array.isArray(raw.data) && raw.data.length) {
      // Blindaje: evitar duplicados en el cache
      const sliced = raw.data.slice(0, Math.max(1, Number(count) || 1));
      const ids = new Set();
      const txt = new Set();
      const safe = [];
      for (const q of sliced) {
        if (!q) continue;
        const id = String(q.id || '').trim();
        const t = String(q.text || '').trim().toLowerCase();
        if (!id || !t) continue;
        if (ids.has(id)) continue;
        if (txt.has(t)) continue;
        ids.add(id);
        txt.add(t);
        safe.push(q);
      }
      const hasLocalizedContent = safe.every((question) => (
        question?.localized?.text?.es &&
        question?.localized?.text?.en &&
        question?.answers?.every((answer) => answer?.label?.es && answer?.label?.en)
      ));
      if (hasLocalizedContent) return safe;
      cachedSelection = safe;
      cachedCategory = raw.category || null;
    }
  } catch {}

  // 2) Construir pool unificado desde las fuentes
  let pool = [];
  for (const url of _TRIVIA_SOURCES) {
    try {
      const data = await _fetchJsonWithTimeout(url, 3500);
      pool = pool.concat(_normalizeQuestions(data));
    } catch {
      // sigue
    }
  }
  if (pool.length === 0) pool = _normalizeQuestions(TRIVIA_LOCAL_FALLBACK);

  // Upgrade legacy Spanish-only daily cache entries in place by stable question ID.
  // This preserves today's selection/order and answer history while making the
  // already-cached game language-reactive.
  if (cachedSelection?.length) {
    const localizedById = new Map(pool.map((question) => [String(question.id), question]));
    const hydrated = cachedSelection.map(
      (question) => localizedById.get(String(question.id)) || normalizeTriviaQuestion(question)
    ).filter(Boolean);
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ day, data: hydrated, category: cachedCategory })
      );
    } catch {}
    return hydrated;
  }

  // 3) Filtros explícitos (si se pasan manualmente)
  if (categories?.length) {
    const set = new Set(categories.map(String));
    pool = pool.filter((q) => set.has(String(q.category)));
  }
  if (difficulties?.length) {
    const set = new Set(difficulties.map(String));
    pool = pool.filter((q) => set.has(String(q.difficulty)));
  }

  const N = Math.max(1, Number(count) || 1);

  // 4) RNG determinístico por (día + usuario)
  const seedStr = `${day}:${safeUid}`;
  const rand = _mulberry32(_hashString(seedStr));

  // 5) Historial de preguntas usadas (por usuario): evita repetición entre días
  let usedQ = _loadUsedQuestions();
  usedQ = _pruneUsedQuestions(usedQ, day, { maxItems: 2000, maxDays: 400 });
  const usedQSet = new Set(usedQ.map((x) => String(x?.id || '').trim()).filter(Boolean));

  // Pool preferido: preguntas NO usadas
  const poolUnused = pool.filter((q) => !usedQSet.has(String(q?.id || '').trim()));

  // 6) Rotación de categorías (por usuario) sobre el pool disponible
  //    Solo la aplicamos cuando NO vienen categories[] manuales.
  let todayCategory = null;
  if (!categories || categories.length === 0) {
    const baseForCategories = poolUnused.length ? poolUnused : pool;
    const allCategories = Array.from(
      new Set(baseForCategories.map((q) => String(q?.category || 'general')))
    );

    // Cargar categorías usadas (por usuario)
    let usedCategories = [];
    try {
      const rawUsed = JSON.parse(localStorage.getItem(USED_CATS_KEY) || '[]');
      if (Array.isArray(rawUsed)) usedCategories = rawUsed.map(String);
    } catch {
      usedCategories = [];
    }

    // Limpiar categorías que ya no existen
    usedCategories = usedCategories.filter((c) => allCategories.includes(String(c)));

    let availableCategories = allCategories.filter((c) => !usedCategories.includes(c));
    if (!availableCategories.length) {
      availableCategories = allCategories.slice();
      usedCategories = [];
    }

    if (availableCategories.length) {
      const idx = Math.floor(rand() * availableCategories.length);
      todayCategory = availableCategories[idx];
    }

    // Persistimos la categoría usada al final (junto con el cache)
  }

  // 7) workingPool: primero intentamos dentro de la categoría elegida y SIN usadas
  const pickFrom = (src) => {
    if (!todayCategory) return src;
    const filtered = src.filter((q) => String(q?.category || 'general') === String(todayCategory));
    return filtered.length ? filtered : src;
  };

  let workingPool = pickFrom(poolUnused.length ? poolUnused : pool);
  if (!workingPool.length) workingPool = pool;

  // 8) Elegir preguntas: shuffle determinístico + garantía de no duplicadas (por id y por texto)
  const uniquePicked = [];
  const seenIds = new Set();
  const seenText = new Set();

  const pushUnique = (q) => {
    const id = String(q?.id || '').trim();
    const t = String(q?.text || '').trim().toLowerCase();
    if (!id || !t) return;
    if (seenIds.has(id)) return;
    if (seenText.has(t)) return;
    seenIds.add(id);
    seenText.add(t);
    uniquePicked.push(q);
  };

  for (const q of _seededShuffle(workingPool, rand)) {
    pushUnique(q);
    if (uniquePicked.length >= N) break;
  }

  // 9) Fallback: si faltan (pool chico por historial), completamos con pool total
  if (uniquePicked.length < N) {
    for (const q of _seededShuffle(pool, rand)) {
      pushUnique(q);
      if (uniquePicked.length >= N) break;
    }
  }

  // 10) Persistir cache del día + categoría usada + ids usados
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ day, data: uniquePicked, category: todayCategory })
    );

    if (todayCategory) {
      // actualizar rotación de categorías por usuario
      let usedCategories = [];
      try {
        const rawUsed = JSON.parse(localStorage.getItem(USED_CATS_KEY) || '[]');
        if (Array.isArray(rawUsed)) usedCategories = rawUsed.map(String);
      } catch {
        usedCategories = [];
      }
      const nextUsed = Array.from(new Set([...usedCategories, todayCategory])).filter(Boolean);
      localStorage.setItem(USED_CATS_KEY, JSON.stringify(nextUsed));
    }

    // Guardar IDs usados (con day) para evitar repetición futura
    const appended = usedQ.concat(uniquePicked.map((q) => ({ id: String(q?.id || '').trim(), day })));
    const pruned = _pruneUsedQuestions(appended, day, { maxItems: 2000, maxDays: 400 });
    _saveUsedQuestions(pruned);
  } catch {
    // ignoramos errores de storage
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[TRIVIA] day:', day, 'uid:', safeUid, 'cat:', todayCategory, 'pool:', pool.length, 'unused:', poolUnused.length, 'picked:', uniquePicked.length);
  }

  return uniquePicked;
}
